import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import SharedNav from './SharedNav';
import Avatar from './shared/Avatar';
import './AccountsPage.css';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

const STATUS_OPTIONS = ['Onboarding', 'Live', 'Paused', 'Cancelled'];
const PAYMENT_OPTIONS = ['Paid', 'Due', 'Overdue'];
const SENTIMENT_COLORS = ['green', 'mustard', 'red'];
const STATUS_ORDER = { Onboarding: 0, Live: 1, Paused: 2, Cancelled: 3 };
const TARGET_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function daysLive(startDate) {
  if (!startDate) return '—';
  const start = new Date(startDate);
  if (isNaN(start)) return '—';
  const diff = Math.floor((Date.now() - start.getTime()) / 86400000);
  return diff < 0 ? 0 : diff;
}

function formatReviewDate(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const STATUS_CLASS = { Onboarding: 'acc-onboarding', Live: 'acc-live', Paused: 'acc-paused', Cancelled: 'acc-cancelled' };
const PAYMENT_CLASS = { Paid: 'pay-paid', Due: 'pay-due', Overdue: 'pay-overdue' };

const EMPTY_FORM = {
  company_id: '', owner_name: '', owner_phone: '', state: '',
  account_status: 'Onboarding', account_start_date: '', pricing: '', payment_status: 'Paid'
};

export default function AccountsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const isAdmin = user.role?.toLowerCase() === 'admin';

  const [accounts, setAccounts] = useState([]);
  const [nonAccounts, setNonAccounts] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [filter, setFilter] = useState('All');
  const [statusColFilter, setStatusColFilter] = useState('All');
  const [paymentColFilter, setPaymentColFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Sort state
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const [viewMode, setViewMode] = useState('table'); // 'table' | 'card'

  // Eye toggle — admin only, persisted in localStorage
  const [showPricing, setShowPricing] = useState(() =>
    isAdmin ? localStorage.getItem('accounts_show_pricing') === 'true' : false
  );

  // Card View: hide Paused accounts, persisted in localStorage
  const [hidePausedCards, setHidePausedCards] = useState(() =>
    localStorage.getItem('accounts_hide_paused_cards') === 'true'
  );

  // Inline edit buffer: { [accId]: { field: value } }
  const [inlineEdits, setInlineEdits] = useState({});
  // Sentiment picker: which row is open
  const [sentimentOpen, setSentimentOpen] = useState(null);

  // Close sentiment picker on outside click
  useEffect(() => {
    if (sentimentOpen === null) return;
    const close = () => setSentimentOpen(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [sentimentOpen]);

  const togglePricing = () => {
    const next = !showPricing;
    setShowPricing(next);
    localStorage.setItem('accounts_show_pricing', String(next));
  };

  const toggleHidePausedCards = () => {
    const next = !hidePausedCards;
    setHidePausedCards(next);
    localStorage.setItem('accounts_hide_paused_cards', String(next));
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const getCellValue = (acc, field) => {
    if (inlineEdits[acc.id] && field in inlineEdits[acc.id]) return inlineEdits[acc.id][field];
    return acc[field] ?? '';
  };

  const setCell = (accId, field, value) => {
    setInlineEdits(p => ({ ...p, [accId]: { ...(p[accId] || {}), [field]: value } }));
  };

  const saveInline = useCallback(async (accId, updates) => {
    try {
      await axios.patch(`/api/accounts/${accId}/inline`, updates);
      setAccounts(prev => prev.map(a =>
        a.id === accId ? { ...a, ...updates, review_date: new Date().toISOString() } : a
      ));
      setInlineEdits(p => {
        const next = { ...p };
        if (next[accId]) {
          const cleared = { ...next[accId] };
          Object.keys(updates).forEach(f => delete cleared[f]);
          if (!Object.keys(cleared).length) delete next[accId];
          else next[accId] = cleared;
        }
        return next;
      });
    } catch (err) {
      console.error('Inline save failed', err);
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/accounts');
      setAccounts(data);
    } catch {}
    setLoading(false);
  }, []);

  const loadNonAccounts = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/accounts/non-accounts');
      setNonAccounts(data);
    } catch {}
  }, []);

  const loadRecruiters = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/accounts/recruiters');
      setRecruiters(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadAccounts();
    loadRecruiters();
  }, [loadAccounts, loadRecruiters]);

  const openAdd = () => {
    loadNonAccounts();
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowModal(true);
  };

  const openEdit = (acc) => {
    setEditing(acc);
    setForm({
      company_id: acc.id,
      owner_name: acc.owner_name || '',
      owner_phone: acc.owner_phone || '',
      state: acc.state || '',
      account_status: acc.account_status || 'Onboarding',
      account_start_date: acc.account_start_date ? acc.account_start_date.split('T')[0] : '',
      pricing: acc.pricing ?? '',
      payment_status: acc.payment_status || 'Paid',
    });
    setError(null);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); setError(null); };

  const handleSave = async () => {
    if (!form.account_status) { setError('Status is required.'); return; }
    if (!editing && !form.company_id) { setError('Select a company.'); return; }
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await axios.put(`/api/accounts/${editing.id}`, form);
      } else {
        await axios.post('/api/accounts', form);
      }
      closeModal();
      loadAccounts();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
    }
    setSaving(false);
  };

  const handleRemove = async (acc) => {
    if (!window.confirm(`Remove "${acc.name}" from Accounts? The company will not be deleted.`)) return;
    try {
      await axios.delete(`/api/accounts/${acc.id}`);
      setAccounts(prev => prev.filter(a => a.id !== acc.id));
    } catch {}
  };

  const onDragEnd = useCallback(async (result) => {
    const { draggableId, destination, source } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    const accountId = parseInt(draggableId);
    const newRecruiter = destination.droppableId;
    const targetCount = accounts.filter(a => a.recruiter === newRecruiter && a.id !== accountId).length;
    if (targetCount >= 6) {
      alert(`${newRecruiter} already has the maximum of 6 accounts.`);
      return;
    }
    setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, recruiter: newRecruiter } : a));
    await saveInline(accountId, { recruiter: newRecruiter });
  }, [accounts, saveInline]);

  const cardGroups = [...recruiters]
    .filter(r => !r.locked_at) // locked recruiters don't get a row in Card View
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(r => {
      const cards = accounts.filter(a => a.recruiter === r.name);
      return {
        recruiter: r,
        cards, // true/unfiltered — drives the "X/6" capacity count
        visibleCards: hidePausedCards ? cards.filter(a => a.account_status !== 'Paused') : cards,
      };
    });

  const f = v => e => setForm(p => ({ ...p, [v]: e.target.value }));

  const truncateName = (name, maxLength = 20) => {
    if (!name || name.length <= maxLength) return name;
    return name.slice(0, maxLength) + '…';
  };

  const filtered = (filter === 'All' ? accounts
    : filter === 'DueOverdue' ? accounts.filter(a => a.account_status === 'Live' && (a.payment_status === 'Due' || a.payment_status === 'Overdue'))
    : accounts.filter(a => a.account_status === filter))
    .filter(a => statusColFilter === 'All' || a.account_status === statusColFilter)
    .filter(a => paymentColFilter === 'All' || a.payment_status === paymentColFilter);

  const visible = [...filtered].sort((a, b) => {
    // Live → Paused → Cancelled always
    const sd = (STATUS_ORDER[a.account_status] ?? 3) - (STATUS_ORDER[b.account_status] ?? 3);
    if (sd !== 0) return sd;
    // Secondary sort by chosen field
    if (sortField) {
      const av = (a[sortField] || '').toLowerCase();
      const bv = (b[sortField] || '').toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    return 0;
  });

  const counts = {
    All: accounts.length,
    Onboarding: accounts.filter(a => a.account_status === 'Onboarding').length,
    Live: accounts.filter(a => a.account_status === 'Live').length,
    Paused: accounts.filter(a => a.account_status === 'Paused').length,
    Cancelled: accounts.filter(a => a.account_status === 'Cancelled').length,
  };

  // Admin + eye-toggle only -- pricing totals for the two new summary boxes.
  const liveTotal = accounts
    .filter(a => a.account_status === 'Live')
    .reduce((sum, a) => sum + (Number(a.pricing) || 0), 0);
  const dueOverdueTotal = accounts
    .filter(a => a.account_status === 'Live' && (a.payment_status === 'Due' || a.payment_status === 'Overdue'))
    .reduce((sum, a) => sum + (Number(a.pricing) || 0), 0);

  const sortIndicator = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const RecruiterSelect = ({ acc, field }) => (
    <select
      className="acc-inline-select"
      value={getCellValue(acc, field) || ''}
      onChange={e => {
        const val = e.target.value;
        setCell(acc.id, field, val);
        saveInline(acc.id, { [field]: val || null });
      }}
    >
      <option value="">—</option>
      {recruiters.map(r => (
        <option key={r.id} value={r.name}>{r.name}</option>
      ))}
    </select>
  );

  const TargetSelect = ({ acc }) => (
    <select
      className="acc-inline-select acc-target-select"
      value={getCellValue(acc, 'target') || ''}
      onChange={e => {
        const val = e.target.value;
        setCell(acc.id, 'target', val);
        saveInline(acc.id, { target: val ? parseInt(val, 10) : null });
      }}
    >
      <option value="">—</option>
      {TARGET_OPTIONS.map(n => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
  );

  // Compares This W against Target: met/exceeded = green, under = amber, no target set = neutral
  const weeklyVsTargetClass = (acc) => {
    if (!acc.target) return '';
    return acc.this_week_sent >= acc.target ? 'acc-week-met' : 'acc-week-under';
  };

  return (
    <div className="acc-page">
      <SharedNav user={user} onLogout={onLogout} />

      <div className="acc-body">

        <div className="acc-summary">
          {['All','Onboarding','Live','Paused','Cancelled'].map(s => (
            <button
              key={s}
              className={`acc-sum-card acc-sum-${s.toLowerCase()} ${filter === s ? 'active' : ''}`}
              onClick={() => setFilter(s)}
            >
              <span className="acc-sum-count">{counts[s]}</span>
              <span className="acc-sum-label">{s}</span>
            </button>
          ))}
          {isAdmin && showPricing && (
            <>
              <button
                className={`acc-sum-card acc-sum-money acc-sum-money-live ${filter === 'Live' ? 'active' : ''}`}
                onClick={() => setFilter('Live')}
                title="Sum of Pricing across all Live accounts"
              >
                <span className="acc-sum-count">${liveTotal.toLocaleString()}</span>
                <span className="acc-sum-label">LIVE TOTAL</span>
              </button>
              <button
                className={`acc-sum-card acc-sum-money acc-sum-money-due ${filter === 'DueOverdue' ? 'active' : ''}`}
                onClick={() => setFilter('DueOverdue')}
                title="Sum of Pricing across Live accounts that are Due or Overdue"
              >
                <span className="acc-sum-count">${dueOverdueTotal.toLocaleString()}</span>
                <span className="acc-sum-label">DUE/OVERDUE TOTAL</span>
              </button>
            </>
          )}
          <div className="acc-summary-right">
            <button
              className={`acc-view-toggle${viewMode === 'card' ? ' acc-view-toggle--active' : ''}`}
              onClick={() => setViewMode(v => v === 'table' ? 'card' : 'table')}
            >
              {viewMode === 'table' ? '⊞ Card View' : '≡ Table View'}
            </button>
            {viewMode === 'card' && (
              <button
                className={`acc-view-toggle${hidePausedCards ? ' acc-view-toggle--active' : ''}`}
                onClick={toggleHidePausedCards}
                title={hidePausedCards ? 'Show Paused accounts' : 'Hide Paused accounts'}
              >
                {hidePausedCards ? '👁 Show Paused' : '🚫 Hide Paused'}
              </button>
            )}
            {isAdmin && (
              <>
                <button
                  className={`acc-eye-btn ${showPricing ? 'acc-eye-on' : ''}`}
                  onClick={togglePricing}
                  title={showPricing ? 'Hide pricing & payment' : 'Show pricing & payment'}
                >
                  {showPricing ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  )}
                </button>
                <button className="acc-add-btn" onClick={openAdd}>+ Add Account</button>
              </>
            )}
          </div>
        </div>

        {viewMode === 'table' && <div className="acc-table-wrap mtn-scroll-x">
          {loading ? (
            <p className="acc-empty">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="acc-empty">
              {statusColFilter !== 'All' || paymentColFilter !== 'All' ? 'No accounts match the selected Status/Payment filter.'
                : filter === 'All' ? 'No accounts yet. Click + Add Account to get started.'
                : filter === 'DueOverdue' ? 'No Live accounts that are Due or Overdue.'
                : `No ${filter} accounts.`}
            </p>
          ) : (
            <table className="acc-table">
              <thead>
                <tr>
                  <th>SENTIMENT</th>
                  <th>CUSTOMER</th>
                  <th>OWNER NAME</th>
                  <th>STATE</th>
                  <th className="acc-th-sortable" onClick={() => toggleSort('recruiter')}>
                    RECRUITER{sortIndicator('recruiter')}
                  </th>
                  <th className="acc-th-sortable" onClick={() => toggleSort('alt_recruiter')}>
                    ALT. REC.{sortIndicator('alt_recruiter')}
                  </th>
                  <th>
                    <div className="acc-th-filter-wrap">
                      STATUS
                      <select
                        className="acc-th-filter"
                        value={statusColFilter}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setStatusColFilter(e.target.value)}
                      >
                        <option value="All">All</option>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </th>
                  <th>START DATE</th>
                  <th>DAYS LIVE</th>
                  {isAdmin && showPricing && <th>ADS</th>}
                  {isAdmin && showPricing && <th>PRICING</th>}
                  {isAdmin && showPricing && (
                    <th>
                      <div className="acc-th-filter-wrap">
                        PAYMENT
                        <select
                          className="acc-th-filter"
                          value={paymentColFilter}
                          onClick={e => e.stopPropagation()}
                          onChange={e => setPaymentColFilter(e.target.value)}
                        >
                          <option value="All">All</option>
                          {PAYMENT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </th>
                  )}
                  {isAdmin && <th></th>}
                  <th>TARGET</th>
                  <th title="Candidates Sent to Client this week (Mon-Sun, Eastern)">THIS W</th>
                  <th title="Candidates Sent to Client last week (Mon-Sun, Eastern)">PREV W</th>
                  <th className="acc-th-notes">NOTES</th>
                  <th>REVIEW DATE</th>
                  <th>PHONE</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(acc => {
                  const statusVal = getCellValue(acc, 'account_status') || acc.account_status;
                  return (
                    <tr key={acc.id}>

                      {/* Sentiment — colored circle picker */}
                      <td className={`acc-sentiment-td${sentimentOpen === acc.id ? ' acc-sentiment-td--open' : ''}`}>
                        <div className="acc-sentiment-cell" onClick={e => e.stopPropagation()}>
                          <button
                            className={`acc-sent-dot acc-sent-${acc.sentiment || 'none'}`}
                            onClick={() => setSentimentOpen(sentimentOpen === acc.id ? null : acc.id)}
                            title="Set sentiment"
                          />
                          {sentimentOpen === acc.id && (
                            <div className="acc-sent-picker">
                              {SENTIMENT_COLORS.map(c => (
                                <button
                                  key={c}
                                  className={`acc-sent-dot acc-sent-${c}`}
                                  onClick={() => { saveInline(acc.id, { sentiment: c }); setSentimentOpen(null); }}
                                />
                              ))}
                              <button
                                className="acc-sent-clear"
                                onClick={() => { saveInline(acc.id, { sentiment: null }); setSentimentOpen(null); }}
                              >✕</button>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="acc-company-name" title={acc.name}>
                        <span className="mtn-name-row"><Avatar name={acc.name} size="sm" />{truncateName(acc.name)}</span>
                      </td>
                      <td>
                        {acc.owner_name
                          ? <span className="mtn-name-row"><Avatar name={acc.owner_name} size="sm" />{acc.owner_name}</span>
                          : '—'}
                      </td>
                      <td>{acc.state || '—'}</td>

                      {/* Recruiter — dropdown */}
                      <td><RecruiterSelect acc={acc} field="recruiter" /></td>

                      {/* Alt. Rec. — dropdown */}
                      <td><RecruiterSelect acc={acc} field="alt_recruiter" /></td>

                      {/* Status — inline select */}
                      <td>
                        <select
                          className={`acc-inline-status ${STATUS_CLASS[statusVal] || ''}`}
                          value={statusVal}
                          onChange={e => {
                            const val = e.target.value;
                            setCell(acc.id, 'account_status', val);
                            saveInline(acc.id, { account_status: val });
                          }}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>

                      <td>{acc.account_start_date ? new Date(acc.account_start_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—'}</td>
                      <td className="acc-days">{daysLive(acc.account_start_date)}</td>

                      {isAdmin && showPricing && (
                        <td className="acc-center">
                          <input
                            type="checkbox"
                            className="acc-ads-checkbox"
                            checked={!!acc.charge_for_ads}
                            onChange={e => saveInline(acc.id, { charge_for_ads: e.target.checked })}
                            title="Charge this customer for ad spend"
                          />
                        </td>
                      )}
                      {isAdmin && showPricing && (
                        <td className="acc-pricing">{acc.pricing != null ? `$${Number(acc.pricing).toLocaleString()}` : '—'}</td>
                      )}
                      {isAdmin && showPricing && (
                        <td>
                          <select
                            className={`acc-inline-status ${PAYMENT_CLASS[acc.payment_status] || ''}`}
                            value={acc.payment_status || ''}
                            onChange={e => saveInline(acc.id, { payment_status: e.target.value })}
                          >
                            {!acc.payment_status && <option value="">—</option>}
                            {PAYMENT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      )}

                      {isAdmin && (
                        <td className="acc-actions">
                          <button className="mtn-btn mtn-btn-edit" onClick={() => openEdit(acc)}><span className="mtn-ic">✏</span>Edit</button>
                          <button className="mtn-btn mtn-btn-delete" onClick={() => handleRemove(acc)}>Remove</button>
                        </td>
                      )}

                      {/* Target — inline dropdown, 1-10 */}
                      <td><TargetSelect acc={acc} /></td>

                      {/* This W / Previous W — system-computed, read-only, flagged against Target */}
                      <td className={`acc-week-cell ${weeklyVsTargetClass(acc)}`}>{acc.this_week_sent}</td>
                      <td className="acc-week-cell">{acc.previous_week_sent}</td>

                      {/* Notes — inline text */}
                      <td className="acc-td-notes">
                        <input
                          className="acc-inline-input"
                          value={getCellValue(acc, 'monthly_exp')}
                          onChange={e => setCell(acc.id, 'monthly_exp', e.target.value)}
                          onBlur={e => {
                            const val = e.target.value;
                            if (val !== (acc.monthly_exp ?? '')) saveInline(acc.id, { monthly_exp: val || null });
                          }}
                          placeholder="—"
                        />
                      </td>

                      <td className="acc-review-date">{formatReviewDate(acc.review_date)}</td>

                      {/* Phone — moved to end */}
                      <td>{acc.owner_phone || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>}

        {viewMode === 'card' && (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="acc-cv-board">
              {cardGroups.map(({ recruiter, cards, visibleCards }) => (
                <div key={recruiter.id} className="acc-cv-row">
                  <div className="acc-cv-row-hdr">
                    <span className="acc-cv-row-name">{recruiter.name}</span>
                    <span className="acc-cv-row-count">{cards.length} / 6</span>
                  </div>
                  <Droppable droppableId={recruiter.name} direction="horizontal">
                    {(provided, snapshot) => (
                      <div
                        className={`acc-cv-cards${snapshot.isDraggingOver ? ' acc-cv-cards--over' : ''}`}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {visibleCards.map((acc, idx) => {
                          const borderColor =
                            acc.sentiment === 'green' ? '#22c55e' :
                            acc.sentiment === 'mustard' ? '#f59e0b' :
                            acc.sentiment === 'red' ? '#ef4444' : '#e2e8f0';
                          const statusVal = getCellValue(acc, 'account_status') || acc.account_status;
                          return (
                            <Draggable key={String(acc.id)} draggableId={String(acc.id)} index={idx}>
                              {(dp, ds) => (
                                <div
                                  className={`acc-cv-card${ds.isDragging ? ' acc-cv-card--drag' : ''}`}
                                  ref={dp.innerRef}
                                  {...dp.draggableProps}
                                  {...dp.dragHandleProps}
                                  style={{ borderLeftColor: borderColor, ...dp.draggableProps.style }}
                                >
                                  <div className="acc-cv-name">{acc.name}</div>
                                  <div className="acc-cv-email">{acc.owner_email || '—'}</div>
                                  <div className="acc-cv-weekly" onClick={e => e.stopPropagation()}>
                                    <span className="acc-cv-target-label">Target</span>
                                    <TargetSelect acc={acc} />
                                    <span className={`acc-cv-week-badge ${weeklyVsTargetClass(acc)}`}>
                                      This W: {acc.this_week_sent}
                                    </span>
                                    <span className="acc-cv-week-badge">
                                      Prev W: {acc.previous_week_sent}
                                    </span>
                                  </div>
                                  <div className="acc-cv-footer">
                                    <span className="acc-cv-state">{acc.state || '—'}</span>
                                    <select
                                      className={`acc-cv-status-sel ${STATUS_CLASS[statusVal] || ''}`}
                                      value={statusVal}
                                      onClick={e => e.stopPropagation()}
                                      onChange={e => {
                                        e.stopPropagation();
                                        const val = e.target.value;
                                        setCell(acc.id, 'account_status', val);
                                        saveInline(acc.id, { account_status: val });
                                      }}
                                    >
                                      {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                        {visibleCards.length === 0 && (
                          <div className="acc-cv-empty-row">
                            {cards.length === 0 ? 'No accounts assigned' : 'All accounts paused (hidden)'}
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </DragDropContext>
        )}

      </div>

      {showModal && (
        <div className="acc-overlay" onClick={closeModal}>
          <div className="acc-modal" onClick={e => e.stopPropagation()}>
            <div className="acc-modal-header">
              <h2>{editing ? `Edit — ${editing.name}` : 'Add Account'}</h2>
              <button className="acc-modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="acc-modal-body">
              {!editing && (
                <div className="acc-field">
                  <label>Company *</label>
                  <select
                    value={form.company_id}
                    onChange={e => {
                      const id = e.target.value;
                      const co = nonAccounts.find(c => String(c.id) === String(id));
                      setForm(p => ({ ...p, company_id: id, owner_name: co?.owner_name || '', owner_phone: co?.owner_phone || '' }));
                    }}
                    className="acc-select"
                  >
                    <option value="">— Select a company —</option>
                    {nonAccounts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              <div className="acc-field-row">
                <div className="acc-field">
                  <label>Owner Name</label>
                  {form.owner_name
                    ? <div className="acc-readonly">{form.owner_name}</div>
                    : <div className="acc-readonly acc-readonly-empty">No user on file</div>}
                </div>
                <div className="acc-field">
                  <label>Owner Phone</label>
                  {form.owner_phone
                    ? <div className="acc-readonly">{form.owner_phone}</div>
                    : <div className="acc-readonly acc-readonly-empty">No user on file</div>}
                </div>
              </div>

              <div className="acc-field-row">
                <div className="acc-field">
                  <label>State</label>
                  <select value={form.state} onChange={f('state')} className="acc-select">
                    <option value="">— State —</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="acc-field">
                  <label>Status *</label>
                  <select value={form.account_status} onChange={f('account_status')} className="acc-select">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="acc-field">
                <label>Start Date</label>
                <input type="date" className="acc-input" value={form.account_start_date} onChange={f('account_start_date')} />
              </div>

              {isAdmin && showPricing && (
                <div className="acc-field-row">
                  <div className="acc-field">
                    <label>Pricing ($/mo)</label>
                    <input type="number" className="acc-input" value={form.pricing} onChange={f('pricing')} placeholder="e.g. 1049" />
                  </div>
                  <div className="acc-field">
                    <label>Payment Status</label>
                    <select value={form.payment_status} onChange={f('payment_status')} className="acc-select">
                      <option value="">— Select —</option>
                      {PAYMENT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {error && <p className="acc-modal-error">{error}</p>}
            </div>

            <div className="acc-modal-footer">
              <button className="acc-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Account'}
              </button>
              <button className="acc-btn-cancel" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
